import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import Button from '../../components/Button';
import { MapPin } from 'lucide-react';
import { getLocationFromCoordinates } from '../../lib/geocoding';
import OnboardingProgress from '../../components/OnboardingProgress';

const schema = z.object({
  location: z.string().min(2, 'Location is required'),
  useLocation: z.boolean(),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingLocation() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const [isGettingLocation, setIsGettingLocation] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      location: user?.location || '',
      useLocation: user?.location_enabled || false,
    },
  });

  const handleGetLocation = () => {
    if ('geolocation' in navigator) {
      setIsGettingLocation(true);
      setError(null);
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const apiKey = import.meta.env.VITE_OPENCAGE_API_KEY;
            if (!apiKey) {
              throw new Error('Location service is not configured');
            }

            const { city, country } = await getLocationFromCoordinates(
              position.coords.latitude,
              position.coords.longitude,
              apiKey
            );

            if (city && country) {
              setValue('location', `${city}, ${country}`);
            } else {
              throw new Error('Could not determine your location');
            }
          } catch (error: any) {
            console.error('Failed to get location:', error);
            setError(error.message || 'Could not determine your location. Please enter it manually.');
          } finally {
            setIsGettingLocation(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          let errorMessage = 'Could not access your location.';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access was denied. Please check your browser settings or enter your location manually.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please enter your location manually.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again or enter your location manually.';
              break;
          }
          
          setError(errorMessage);
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setError('Geolocation is not supported by your browser. Please enter your location manually.');
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      await updateProfile({
        location: data.location,
        location_enabled: data.useLocation,
      });
      navigate('/onboarding/experience');
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError('Failed to save your location. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="location" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl text-center font-bold font-dillan text-primary mb-2">
          Where are you based?
        </h1>
        <p className="text-neutral text-center text-l mb-6">
          While music is global, knowing your location helps connect you with local opportunities and nearby collaborators.
          We only use your location to enhance your experience and will never share it without your explicit consent.
        </p>
        {error && (
          <p className="text-sm text-primary bg-secondary-dark bg-opacity-10 p-3 rounded-full">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="location" className="block text-sm text-primary mb-2 pl-4">
              Your Location
            </label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral" />
              <input
                type="text"
                id="location"
                {...register('location')}
                className="mb-4 w-full h-12 pl-10 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
                placeholder="City, Country"
              />
            </div>
            {errors.location && (
              <p className="mt-1 text-sm text-primary">{errors.location.message}</p>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            className="mb-2 w-full text-surface-light bg-neutral border-neutral hover:bg-surface hover:border-surface hover:text-neutral"
            onClick={handleGetLocation}
            disabled={isGettingLocation}
          >
            {isGettingLocation ? 'Getting location...' : 'Use Current Location'}
          </Button>



          <label className="flex items-center space-x-3 pl-4">
            <input
              type="checkbox"
              {...register('useLocation')}
              className="w-4 h-4 pl-2 border-2 border-neutral rounded-full checked:bg-neutral checked:border-neutral focus:ring-accent appearance-none transition-all"
            />
            <span className="text-sm text-neutral">
              Enable location services to find nearby opportunities
            </span>
          </label>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-8"
              onClick={() => navigate('/onboarding/contact')}
            >
              Back
            </Button>
            <Button type="submit" variant="primary" className="flex-1 text-surface-light mt-8">
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}